import {useAuth} from '@apps-common/auth-react';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import DownloadIcon from '@mui/icons-material/GetApp';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapPinIcon from '@mui/icons-material/Place';
import UploadIcon from '@mui/icons-material/Publish';
import CoverageIcon from '@mui/icons-material/Texture';
import {
  Box,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import GeoJSON from 'ol/format/GeoJSON';
import PropTypes from 'prop-types';
import React, {Fragment, useRef} from 'react';
import {connect} from 'react-redux';
import {
  set as setGeometry,
  setByFile as setGeometryByFile,
} from '../actions/geometry.js';
import {
  setExtent as setMapExtent,
  setView as setMapView,
} from '../actions/map.js';
import {reset as resetSelectedScene} from '../actions/selected-scene.js';
import setActiveQuad from '../actions/set-active-quad.js';
import {drawModes, modes, setDrawMode, setMode} from '../actions/ui.js';
import {trackEvent} from '../analytics.js';
import {ACCEPTED_FILE_TYPES, MIN_POINT_ZOOM_LEVEL} from '../common.js';
import {getActiveQuad, getSelectedMosaic} from '../selectors/index.js';
import geometryToCoords from '../util/geometry-to-coords.js';
import {getTimeInfo, isGlobal} from '../util/mosaic.js';
import {addQueryParams} from '../util/url.js';
import {Chip, Thumbnail} from './list.jsx';
import Panel from './panel.jsx';
import QuadList from './quad-list.jsx';
import SceneListItem from './scene-list-item.jsx';
import SceneList from './scene-list.jsx';

const SelectedCoordinate = styled(Typography)({
  fontSize: '14px',
});

const FlexWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden',
});

const BackButton = styled(IconButton)({
  marginLeft: -16,
  marginRight: 8,
  padding: 0,
  color: 'white',
});

const NavToggleButton = ({value, onClick, dataQe, Icon, label}) => (
  <ToggleButton size="small" value={value} onClick={onClick} data-qe={dataQe}>
    <Stack direction="row" sx={{alignItems: 'center', gap: 0.5}}>
      <Icon fontSize="small" />
      {label}
    </Stack>
  </ToggleButton>
);

NavToggleButton.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onClick: PropTypes.func,
  dataQe: PropTypes.string,
  Icon: PropTypes.object,
};

const format = new GeoJSON();

const SelectionMode = props => {
  const auth = useAuth();
  const fileInputRef = useRef();

  const onUploadClick = () => {
    trackEvent('Upload file selected');
    fileInputRef.current.click();
  };

  const onFileChange = event => {
    const file = event.target.files[0];
    trackEvent('File uploaded');
    props.setGeometryByFile(file);
  };

  const onPointClick = () => {
    trackEvent('Select point selected');
    props.setDrawMode(drawModes.POINT);
  };

  const onAreaClick = () => {
    trackEvent('Draw area selected');
    props.setDrawMode(drawModes.AREA);
  };

  const zoomToGeometry = () => {
    if (props.geometry.type === 'Point') {
      props.setMapView({
        center: props.geometry.coordinates,
        zoom: Math.max(MIN_POINT_ZOOM_LEVEL, props.zoom),
      });
      return;
    }

    const geometry = format.readGeometry(props.geometry);
    props.setMapExtent(geometry.getExtent());
  };

  const zoomToQuad = () => {
    props.setMapExtent(props.activeQuad.bbox);
  };

  const clearGeometry = () => {
    props.setGeometry(null);
    props.resetSelectedScene();
  };

  const exitQuad = () => {
    props.setActiveQuad(null);
  };

  const setModeNone = () => {
    props.setMode(modes.NONE);
  };

  const renderContent = () => {
    if (!props.geometry) {
      return (
        <Box
          sx={{
            color: 'text.secondary',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5">Go beyond the basemap</Typography>
          <br />
          <Typography data-qe="select-point-message">
            Select a point or draw an area on the map to see the scenes and
            quads that overlap your selection.
          </Typography>
        </Box>
      );
    } else if (!props.activeQuad) {
      return <QuadList />;
    }

    const secondaryAction = (
      <>
        <Tooltip title="Zoom to Quad">
          <IconButton onClick={zoomToQuad} data-qe="zoom-to-quad">
            <MyLocationIcon />
          </IconButton>
        </Tooltip>
        {props.activeQuad._links && props.activeQuad._links.download && (
          <Tooltip title="Download Quad">
            <IconButton
              data-qe="download-quad"
              component={Link}
              href={addQueryParams(props.activeQuad._links.download, {
                token: auth.user?.accessToken,
              })}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
      </>
    );

    return (
      <FlexWrapper>
        <Box>
          <Paper
            elevation={1}
            square
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title="Back to Quads List">
              <IconButton onClick={exitQuad} data-qe="clear-quad" size="large">
                <ChevronLeft />
              </IconButton>
            </Tooltip>

            <Typography
              variant="overline"
              sx={{
                display: 'inline',
              }}
            >
              Selected Quad
            </Typography>
          </Paper>

          <Divider />

          <List disablePadding>
            <ListItem secondaryAction={secondaryAction}>
              <Thumbnail src={props.activeQuad._links.thumbnail} mr={2} />
              <ListItemText disableTypography>
                <Box
                  sx={{
                    mb: 0.5,
                  }}
                >
                  <Tooltip title="Quad ID">
                    <Typography
                      variant="body1"
                      noWrap
                      sx={{
                        display: 'inline',
                      }}
                    >
                      {props.activeQuad.id}
                    </Typography>
                  </Tooltip>
                </Box>
                <Tooltip title="Quad Coverage">
                  <Chip
                    size="small"
                    icon={<CoverageIcon />}
                    label={`${props.activeQuad.percent_covered}%`}
                  />
                </Tooltip>
              </ListItemText>
            </ListItem>
          </List>

          <Divider />
        </Box>

        <SceneList />
      </FlexWrapper>
    );
  };

  const renderHeadingInner = () => {
    if (!props.mosaic) {
      return (
        <Typography variant="subtitle1" color="inherit" noWrap>
          No mosaic selected
        </Typography>
      );
    }

    const timeInfo = getTimeInfo(props.mosaic);
    const type = isGlobal(props.mosaic) ? 'Global' : 'Regional';
    return (
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Typography
          variant="subtitle1"
          color="inherit"
          noWrap
          data-qe="header-title"
        >
          {timeInfo} ({type})
        </Typography>
        <Typography
          component="div"
          variant="caption"
          noWrap
          data-qe="header-description"
          sx={{
            color: 'text.secondary',
          }}
        >
          {props.mosaic.name}
        </Typography>
      </Box>
    );
  };

  const renderSceneAtLocation = () => {
    let selectedSceneNotFound = false;
    if (props.geometry) {
      if (props.selectedScene) {
        return (
          <Box
            sx={theme => ({
              backgroundColor: 'background.paper',
              ...theme.applyStyles('light', {backgroundColor: 'white'}),
            })}
          >
            <Divider />

            <List disablePadding>
              <SceneListItem scene={props.selectedScene} />
            </List>
          </Box>
        );
      }

      selectedSceneNotFound = true;
    }

    if (selectedSceneNotFound || !props.provenanceData) {
      if (selectedSceneNotFound && props.selectedSceneLoading) {
        return (
          <Box
            sx={{
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="caption">
              <i>Loading...</i>
            </Typography>
          </Box>
        );
      }

      return (
        <Box
          sx={{
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="caption" data-qe="no-scene-message">
            <i>No source scene found at location</i>
          </Typography>
        </Box>
      );
    }
  };

  const renderLocation = () => {
    if (props.geometry) {
      return (
        <Box>
          <Paper
            elevation={0}
            square
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 1,
              pl: 2,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{color: 'text.secondary'}}>
                Selected location
              </Typography>
              <SelectedCoordinate data-qe="location-coordinates">
                {geometryToCoords(props.geometry).join(', ')}
              </SelectedCoordinate>
            </Box>

            <Box>
              <Tooltip title="Zoom to location">
                <IconButton onClick={zoomToGeometry} data-qe="zoom-to-location">
                  <MyLocationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear location">
                <IconButton onClick={clearGeometry} data-qe="clear-location">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {props.geometry.type === 'Point' && !props.activeQuad && (
            <Paper elevation={1} square>
              <Divider />

              <Box
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                  }}
                >
                  Scene at selected location
                </Typography>
              </Box>

              {renderSceneAtLocation()}
            </Paper>
          )}

          <Divider />
        </Box>
      );
    }
  };

  return (
    <Fragment>
      <Panel open={props.open}>
        <Paper
          elevation={2}
          square
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            color: 'white',
            alignItems: 'center',
          }}
        >
          <BackButton
            color="default"
            size="small"
            onClick={setModeNone}
            data-qe="back-button"
          >
            <ChevronLeft fontSize="large" />
          </BackButton>

          {renderHeadingInner()}
        </Paper>

        <Paper elevation={1} square sx={{padding: 2}}>
          <ToggleButtonGroup
            color="primary"
            value={props.drawMode ?? drawModes.POINT}
          >
            <NavToggleButton
              label="Select point"
              value={drawModes.POINT}
              onClick={onPointClick}
              dataQe="select-point-button"
              Icon={MapPinIcon}
            />

            <NavToggleButton
              label="Draw area"
              value={drawModes.AREA}
              onClick={onAreaClick}
              dataQe="draw-area-button"
              Icon={CropIcon}
            />

            <NavToggleButton
              label="Upload file"
              value="" // Not used but still required
              onClick={onUploadClick}
              dataQe="upload-file-button"
              Icon={UploadIcon}
            />
          </ToggleButtonGroup>

          <input
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={onFileChange}
            ref={fileInputRef}
            style={{display: 'none'}}
            type="file"
            data-qe="upload-file-input"
          />
        </Paper>

        <FlexWrapper>
          {renderLocation()}
          {renderContent()}
        </FlexWrapper>
      </Panel>
    </Fragment>
  );
};

SelectionMode.propTypes = {
  activeQuad: PropTypes.object,
  drawMode: PropTypes.string.isRequired,
  geometry: PropTypes.object,
  mosaic: PropTypes.object,
  provenanceData: PropTypes.string,
  selectedScene: PropTypes.object,
  selectedSceneLoading: PropTypes.bool.isRequired,
  zoom: PropTypes.number,
  open: PropTypes.bool,

  resetSelectedScene: PropTypes.func,
  setActiveQuad: PropTypes.func,
  setDrawMode: PropTypes.func,
  setGeometry: PropTypes.func,
  setGeometryByFile: PropTypes.func,
  setMapExtent: PropTypes.func,
  setMapView: PropTypes.func,
  setMode: PropTypes.func,
};

const mapStateToProps = state => ({
  activeQuad: getActiveQuad(state),
  drawMode: state.ui.drawMode,
  geometry: state.geometry.json,
  mosaic: getSelectedMosaic(state),
  provenanceData: state.provenanceData,
  selectedScene: state.selectedScene.properties,
  selectedSceneLoading: state.selectedScene.loading,
  zoom: state.map.zoom,
});

const mapDispatchProps = {
  resetSelectedScene,
  setActiveQuad,
  setDrawMode,
  setGeometry,
  setGeometryByFile,
  setMapExtent,
  setMapView,
  setMode,
};

export default connect(mapStateToProps, mapDispatchProps)(SelectionMode);
